// Outreach: who to write to, and the sequence that writes to them (Phase 4, item 6).
//
// People on top -- the best person as a card, everyone else as a compact ranked table whose
// detail lives in hover cards (no click-to-expand) -- then "find more contacts", then one
// sequence bar: New sequence opens the builder (GRO-287, P4-07) seeded with the selection, and
// Add to existing opens the board's push panel. The push panel and the builder each keep their
// own guards: a preview pushes nothing and creates a dry run.
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { acceptContactDetails, findContactDetails, panelMessage, readViewAs } from '../api/client';
import type { TokenGetter } from '../api/client';
import type { BookAccount, BookPayload, Contact, EnrichCandidate } from '../api/types';
import { useAuthContext } from '../auth/AuthContext';
import { useChatStore } from '../chat/ChatProvider';
import { CardRows, CardTitle, HoverCard } from '../components/HoverCard';
import { chatActorEmail } from '../lib/chatThread.ts';
import { personNameFromEmail } from '../lib/contactName';
import { playgroundUntickableReason, sendableContacts } from '../lib/playgroundContacts.ts';
import { loadThread, sendMessage, threadKeyFor } from '../lib/chatStore.ts';
import { formatCredits, httpsUrl } from '../lib/format';
import { EnrichControl } from './EnrichControl';
import type { EnrichState } from './EnrichControl';
import { contactSignalLabel } from './heroLines';
import { orgDisplayName } from './historyData';
import { OutreachContext } from './OutreachContext';
import { TAG_MEANINGS, foundByScoutRows, foundLabel, nameNote, orgBadge, outreachSplit, personChip, reachOf } from './outreachPeople';
import { isQ4Hero } from '../lib/q4Target';
import type { OrgBadge, OutreachPerson } from './outreachPeople';
import { PanelSection, ReachMarks } from './panelParts';
import { canPushAccount, enrichLookupKey, enrichOffered, pushBlockedReason, pushableContacts } from './permissions';
import { PushPanel } from './PushPanel';
import { SequenceTab } from './SequenceTab';

// --- Hover cards -----------------------------------------------------------------------------

function PersonCard({ person, badge }: { person: OutreachPerson; badge: OrgBadge | null }) {
  const rows: Array<[string, ReactNode]> = [];
  const note = nameNote(person);
  if (note) rows.push(['Name', note]);
  if (person.score != null) rows.push(['Score', String(person.score)]);
  if (person.email) rows.push(['Email', person.email]);
  if (person.contact?.phone) rows.push(['Phone', person.contact.phone]);
  if (person.linkedin) {
    rows.push(['LinkedIn', <a key="li" href={httpsUrl(person.linkedin) ?? undefined} target="_blank" rel="noopener">Profile ↗</a>]);
  }
  if (person.contact?.last_activity) rows.push(['Last activity', person.contact.last_activity]);
  if (person.sequence) rows.push(['Sequence', person.sequence]);
  if (person.contact?.gong_flow) rows.push(['Gong flow', [person.contact.gong_flow, person.contact.gong_flow_status].filter(Boolean).join(' — ')]);
  if (badge?.orgName) rows.push(['Cartesia', `${orgDisplayName(badge.orgName)}${badge.plan ? `, ${badge.plan}` : ''}`]);
  if (person.foundByScout) rows.push(...foundByScoutRows(person.foundByScout));
  return (
    <>
      <CardTitle>{person.name}</CardTitle>
      {person.title ? <p className="hcnote">{person.title}{person.contact?.department ? ` · ${person.contact.department}` : ''}</p> : null}
      {person.because ? <p className="hcprose">{person.because}</p> : null}
      {person.contact?.verification_reason ? <p className="hcprose">{person.contact.verification_reason}</p> : null}
      {rows.length ? <CardRows rows={rows} /> : null}
      {person.flags.length ? <p className="hcwarn">{person.flags.join(' · ')}</p> : null}
    </>
  );
}

function NameHover({ person, badge }: { person: OutreachPerson; badge: OrgBadge | null }) {
  return (
    <HoverCard label={`About ${person.name}`} card={() => <PersonCard person={person} badge={badge} />}>
      <span className={'personname' + (person.named ? '' : ' unnamed')}>{person.name}</span>
    </HoverCard>
  );
}

function TagHover({ kind }: { kind: string }) {
  const meaning = TAG_MEANINGS[kind];
  const chip = <span className={'persontag' + (kind === 'replied' || kind === 'warm_path' ? ' warm' : '')}>{contactSignalLabel(kind)}</span>;
  if (!meaning) return chip;
  return <HoverCard label={contactSignalLabel(kind)} card={() => <p className="hcprose">{meaning}</p>}>{chip}</HoverCard>;
}

/** A table row's one chip: a flag, else the sequence they are in, else their first signal. */
function RowChip({ person }: { person: OutreachPerson }) {
  const chip = personChip(person);
  if (!chip) return null;
  if (chip.kind === 'flag') return <span className="persontag flag">{chip.text}</span>;
  if (chip.kind === 'sequence') return <span className="persontag" title={`In ${chip.text}`}>in a sequence</span>;
  if (chip.kind === 'scout' && person.foundByScout) {
    const found = person.foundByScout;
    const label = foundLabel(found);
    const hoverLabel = label === 'Q4' ? 'From Q4 research' : label === 'Enriched' ? 'Enriched from LinkedIn' : 'Found by Scout';
    return (
      <HoverCard label={hoverLabel} card={() => <CardRows rows={foundByScoutRows(found)} />}>
        <span className="persontag scout">{label}{chip.text ? ` · ${chip.text}` : ''}</span>
      </HoverCard>
    );
  }
  return <TagHover kind={chip.text} />;
}

function BadgeHover({ badge }: { badge: OrgBadge }) {
  const rows: Array<[string, string]> = [];
  if (badge.orgName) rows.push(['Org', orgDisplayName(badge.orgName)]);
  if (badge.plan) rows.push(['Plan', badge.plan]);
  if (badge.role) rows.push(['Role', badge.role]);
  if (badge.credits30 != null) rows.push(['Credits 30d', formatCredits(badge.credits30)]);
  if (badge.lastUsage) rows.push(['Last used', badge.lastUsage]);
  if (badge.users != null) rows.push(['People here', String(badge.users)]);
  return (
    <HoverCard label={badge.kind === 'org' ? 'Cartesia org' : 'Playground account'} card={() => (
      <>
        <CardTitle>{badge.kind === 'org' ? 'Cartesia org' : 'Playground account'}</CardTitle>
        <CardRows rows={rows} />
      </>
    )}>
      <span className={'orgbadge' + (badge.kind === 'playground' ? ' playground' : '')}>{badge.label}</span>
    </HoverCard>
  );
}

// --- People ----------------------------------------------------------------------------------

interface PeopleControls {
  book: BookPayload;
  row: BookAccount;
  people: OutreachPerson[];
  selection: ReadonlySet<string>;
  onToggle: (contactId: string, checked: boolean) => void;
  enrichState: EnrichState | null;
  onFind: (contactId: string) => void;
}

/**
 * The select box, for a person this viewer may push: a contact, or a playground person by their
 * stand-in. When something blocks them the box is shown disabled and says why on hover.
 */
function SelectBox({ controls, person }: { controls: PeopleControls; person: OutreachPerson }) {
  if (!canPushAccount(controls.book, controls.row)) return null;
  const target = person.contact ?? person.findTarget;
  const reason = target ? pushBlockedReason(target) : playgroundUntickableReason(person.hero);
  if (reason) {
    const why = `Can't be ticked: ${reason}.`;
    return (
      <span className="selectwhy" title={why}>
        <input type="checkbox" aria-label={`${person.name}: ${why}`} disabled checked={false} readOnly />
      </span>
    );
  }
  if (!target) return null;
  return (
    <input type="checkbox" aria-label={`Select ${person.name}`} checked={controls.selection.has(target.id)}
      onChange={event => controls.onToggle(target.id, event.target.checked)} />
  );
}

/**
 * "Find" where the existing lookup applies: no address, or an address and no name. A
 * playground person with no contact is looked up by their stand-in (`findTarget`).
 */
function FindButton({ controls, target }: { controls: PeopleControls; target: Contact | null }) {
  if (!target || !enrichOffered(controls.book, controls.row, target)) return null;
  if (controls.enrichState?.contactId === target.id) return null;
  const key = enrichLookupKey(target);
  const title = key === 'email' ? 'Find their name by address: their Clerk sign-up first, then Amplemarket'
    : key === 'linkedin' ? 'Find their email in Amplemarket, by LinkedIn' : 'Find their details in Amplemarket, by name and company';
  const label = key === 'email' ? 'Find name' : 'Find';
  return <button type="button" className="findbtn" title={title} onClick={() => controls.onFind(target.id)}>{label}</button>;
}

function BestPerson({ person, controls }: { person: OutreachPerson; controls: PeopleControls }) {
  const badge = orgBadge(person, controls.people);
  const reach = reachOf(person);
  return (
    <div className="bestperson">
      <SelectBox controls={controls} person={person} />
      <div className="bestmain">
        <div className="bestline">
          <NameHover person={person} badge={badge} />
          {badge ? <BadgeHover badge={badge} /> : null}
          {person.title ? <span className="besttitle">{person.title}</span> : null}
        </div>
        <div className="besttags">
          {person.tags.map(kind => <TagHover key={kind} kind={kind} />)}
          <ReachMarks email={reach.email} linkedin={reach.linkedin} />
          <FindButton controls={controls} target={person.findTarget} />
        </div>
        {person.because ? <p className="bestwhy">{person.because}</p> : null}

        {/* Stops and a running sequence sit on the card itself, not only in its hover. The
            hero's reason already names a sequence it knows about, so it is not said twice. */}
        {person.flags.length ? <p className="bestwarn">{person.flags.join(' · ')}</p> : null}
        {person.sequence && !person.because?.includes(person.sequence) ? <p className="bestwhy">Already in {person.sequence}</p> : null}
      </div>
      {person.score != null ? <span className="bestscore" title="Hero score">{person.score}</span> : null}
    </div>
  );
}

function PeopleTable({ people, controls, onAccept, onDismiss }: {
  people: OutreachPerson[];
  controls: PeopleControls;
  onAccept: () => void;
  onDismiss: () => void;
}) {
  return (
    <table className="peopletable">
      <thead>
        <tr><th aria-label="Select" /><th>Name</th><th className="ptitlecol">Title</th><th className="ptagcol">Tags</th><th>Reach</th><th aria-label="Find" /></tr>
      </thead>
      <tbody>
        {people.map(person => {
          const badge = orgBadge(person, controls.people);
          const reach = reachOf(person);
          const target = person.findTarget;
          const enrichingHere = target && controls.enrichState?.contactId === target.id;
          return [
            <tr key={person.key}>
              <td className="pselect"><SelectBox controls={controls} person={person} /></td>
              <td className="pnamecell">
                <NameHover person={person} badge={badge} />{badge ? <BadgeHover badge={badge} /> : null}
                {/* A Q4 hero's reason sits on the row: it is why the strategist picked them. */}
                {isQ4Hero(person.foundByScout) && person.because
                  ? <span className="q4reason" title={person.because}>{person.because}</span> : null}
              </td>
              <td className="ptitlecol">{person.title || ''}</td>
              <td className="ptagcol"><RowChip person={person} /></td>
              <td><ReachMarks email={reach.email} linkedin={reach.linkedin} /></td>
              <td className="pfind"><FindButton controls={controls} target={target} /></td>
            </tr>,

            // The lookup's answer needs a human to press Use, so it gets the row's full width.
            enrichingHere && target ? (
              <tr key={person.key + ':enrich'} className="penrich">
                <td colSpan={6}>
                  <EnrichControl contact={target} state={controls.enrichState} onFind={() => controls.onFind(target.id)}
                    onAccept={onAccept} onDismiss={onDismiss} />
                </td>
              </tr>
            ) : null,
          ];
        })}
      </tbody>
    </table>
  );
}

/** Sequences more than one person is already in, said once above the table. */
function sharedSequences(contacts: Contact[]): [string, number][] {
  const counts = new Map<string, number>();
  contacts.forEach(contact => { if (contact.sequence) counts.set(contact.sequence, (counts.get(contact.sequence) ?? 0) + 1); });
  return [...counts.entries()].filter(([, count]) => count > 1).sort((first, second) => second[1] - first[1]);
}

// --- Find more contacts ----------------------------------------------------------------------

/** Sends the ask to the account's Scout thread (the find_contacts path) and opens the Scout tab. */
function FindMoreContacts({ row, onSent }: { row: BookAccount; onSent: () => void }) {
  const { email } = useAuthContext();
  const store = useChatStore();
  const [text, setText] = useState('');

  // History first, so the ask lands after the thread's earlier messages rather than racing them.
  async function send(ask: string) {
    const thread = { account_id: row.id };
    const threadKey = threadKeyFor(chatActorEmail(email, readViewAs()), thread);
    await loadThread(store, threadKey, thread);
    await sendMessage(store, threadKey, thread, `Find more contacts at ${row.account}: ${ask}`);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const ask = text.trim();
    if (!ask) return;
    void send(ask);
    setText('');
    onSent();
  }

  return (
    <form className="findmore" onSubmit={submit}>
      <input aria-label="Find more contacts" placeholder="Find more contacts, e.g. 'speech engineers' or 'ops leads in Austin'"
        value={text} onChange={event => setText(event.target.value)} />
      <button type="submit" className="btn" disabled={!text.trim()}>Find</button>
    </form>
  );
}

// --- The tab ---------------------------------------------------------------------------------

/**
 * What a lookup that found nothing says. A name lookup by address falls back to the name read
 * off a first.last address, which the row already shows, so the person knows it stays.
 */
function missMessage(target: Contact | null): string {
  if (!target || enrichLookupKey(target) !== 'email') return 'Amplemarket has no address for this person.';
  const derived = personNameFromEmail(target.email);
  return derived ? `Amplemarket has no name for this person, so they stay ${derived}, read from the address.`
    : 'Amplemarket has no name for this person.';
}

export function OutreachTab({ row, book, getToken, active, onContactEnriched, onOpenScout }: {
  row: BookAccount;
  book: BookPayload;
  getToken: TokenGetter;
  active: boolean;
  onContactEnriched: (contactId: string, candidate: EnrichCandidate) => void;
  onOpenScout: () => void;
}) {
  const [selection, setSelection] = useState<ReadonlySet<string>>(() => new Set());
  const [enrichState, setEnrichState] = useState<EnrichState | null>(null);
  const [openPanel, setOpenPanel] = useState<'builder' | 'push' | null>(null);
  // The selection as of the last New sequence click that opened the builder; null until then.
  // Each opening passes it again, so people ticked since (after a Find, on the best-person card)
  // arrive picked without re-ticking them in the builder.
  const [builderSeed, setBuilderSeed] = useState<string[] | null>(null);

  // Shared inboxes (sales@, support@) never take the best-person card; they sit collapsed below.
  // People Scout found get their own section, so a stranger never takes the card either.
  const { people, foundByScout, q4Heroes, sharedInboxes } = outreachSplit(row);
  const everyone = [...people, ...foundByScout, ...q4Heroes, ...sharedInboxes];
  const [best, ...rest] = people;
  const pushable = pushableContacts(book, row);
  // The builder lists and resolves playground people by their stand-ins, alongside the contacts.
  const sendableRow = useMemo(() => ({ ...row, contacts: sendableContacts(row) }), [row]);
  const selected = pushable.filter(contact => selection.has(contact.id));
  const selectedKey = selected.map(contact => contact.id).join(',');

  // Ticked while the builder is open: the builder picks them up too (it only ever adds).
  useEffect(() => {
    if (openPanel === 'builder') setBuilderSeed(selectedKey ? selectedKey.split(',') : []);
  }, [openPanel, selectedKey]);
  const shared = sharedSequences(row.contacts);
  const mayPush = canPushAccount(book, row);

  // The existing contact lookup (Amplemarket), held server-side until a human presses Use.
  async function runEnrich(contactId: string) {
    setEnrichState({ contactId, status: 'loading', candidate: null, message: '' });
    try {
      const payload = await findContactDetails(getToken, { account_id: row.id, contact_id: contactId });
      const target = everyone.find(person => person.findTarget?.id === contactId)?.findTarget ?? null;
      setEnrichState(payload.found && payload.candidate
        ? { contactId, status: 'found', candidate: payload.candidate, message: '' }
        : { contactId, status: 'missing', candidate: null, message: missMessage(target) });
    } catch (error) {
      setEnrichState({ contactId, status: 'missing', candidate: null, message: panelMessage(error, 'The lookup failed.') });
    }
  }

  async function acceptEnrich() {
    if (enrichState?.status !== 'found' || !enrichState.candidate) return;
    const { contactId, candidate } = enrichState;
    try {
      await acceptContactDetails(getToken, { account_id: row.id, contact_id: contactId });
      onContactEnriched(contactId, candidate);
      setEnrichState(null);
    } catch (error) {
      setEnrichState({ contactId, status: 'missing', candidate: null, message: panelMessage(error, 'Could not accept it.') });
    }
  }

  const controls: PeopleControls = {
    book, row, people: everyone, selection, enrichState,
    onToggle: (contactId, checked) => setSelection(current => {
      const next = new Set(current);
      if (checked) next.add(contactId);
      else next.delete(contactId);
      return next;
    }),
    onFind: contactId => void runEnrich(contactId),
  };

  // Opening passes the selection (the effect above); closing only hides the builder.
  function openBuilder() {
    setOpenPanel(panel => panel === 'builder' ? null : 'builder');
  }

  return (
    <>
      <OutreachContext row={row} />

      <PanelSection title="Best person">
        {best ? <BestPerson person={best} controls={controls} /> : <p className="panelempty">No people on this account yet. Ask Scout to find some below.</p>}
      </PanelSection>

      {rest.length ? (
        <PanelSection title="Everyone else" aside={`${rest.length} · ranked`}>
          {shared.length ? (
            <p className="sharedseq">
              In Amplemarket: {shared.map(([sequence, count]) => `${count} in ${sequence}`).join(' · ')}
            </p>
          ) : null}
          <PeopleTable people={rest} controls={controls} onAccept={() => void acceptEnrich()} onDismiss={() => setEnrichState(null)} />
        </PanelSection>
      ) : null}

      {/* Found by Scout's find_contacts, and people enriched from LinkedIn in the extension
          (chip "Enriched"): listed at once, whatever Salesforce did with the save, and tickable
          for a sequence like anyone else once Salesforce saved them. */}
      {foundByScout.length ? (
        <PanelSection title="Found by Scout" aside={`${foundByScout.length} · newest first`}>
          <PeopleTable people={foundByScout} controls={controls} onAccept={() => void acceptEnrich()} onDismiss={() => setEnrichState(null)} />
        </PanelSection>
      ) : null}

      {/* The Q4 research's heroes: the strategists' picks, not in Salesforce yet, so not sendable
          until they are saved (the same rule as a blocked Scout find). */}
      {q4Heroes.length ? (
        <PanelSection title="From Q4 research" aside={`${q4Heroes.length} · the strategists' picks`}>
          <PeopleTable people={q4Heroes} controls={controls} onAccept={() => void acceptEnrich()} onDismiss={() => setEnrichState(null)} />
        </PanelSection>
      ) : null}

      {/* Team and role addresses are not people to write to, so they stay folded away. */}
      {sharedInboxes.length ? (
        <details className="sharedinboxes">
          <summary>Shared inboxes · {sharedInboxes.length}</summary>
          <PeopleTable people={sharedInboxes} controls={controls} onAccept={() => void acceptEnrich()} onDismiss={() => setEnrichState(null)} />
        </details>
      ) : null}

      {/* The best person's lookup answers under the card, the same as a table row's. */}
      {best?.findTarget && enrichState?.contactId === best.findTarget.id ? (
        <EnrichControl contact={best.findTarget} state={enrichState} onFind={() => void runEnrich(best.findTarget!.id)}
          onAccept={() => void acceptEnrich()} onDismiss={() => setEnrichState(null)} />
      ) : null}

      {book.viewer.sees_chat_embed ? <FindMoreContacts row={row} onSent={onOpenScout} /> : null}

      <PanelSection title="Sequence">
        {book.integrations?.amplemarket && book.viewer.can_push && !mayPush ? (
          <p className="panelempty">This account is owned by {row.sdr_owner || 'another SDR'}. You can sequence people on your own accounts.</p>
        ) : (
          <div className="seqbar">
            <span className="seqcount"><b>{selected.length}</b> selected</span>
            {book.viewer.can_push ? (
              <button type="button" className={'btn' + (openPanel === 'builder' ? ' on' : '')} aria-expanded={openPanel === 'builder'}
                onClick={openBuilder}>New sequence</button>
            ) : null}
            {mayPush ? (
              <>
                <span className="muted">or</span>
                <button type="button" className={'btn' + (openPanel === 'push' ? ' on' : '')} aria-expanded={openPanel === 'push'}
                  disabled={!selected.length} onClick={() => setOpenPanel(panel => panel === 'push' ? null : 'push')}>
                  Add to existing
                </button>
              </>
            ) : null}
            {!book.viewer.can_push ? <span className="muted">Sequencing is for strategists.</span> : null}
          </div>
        )}

        {openPanel === 'push' && selected.length ? (
          <PushPanel row={row} selected={selected} getToken={getToken} onClose={() => setOpenPanel(null)} />
        ) : null}
        {/* Mounted from the first open and only hidden after, so switching to Add to existing
            and back keeps unsaved edits. */}
        {builderSeed !== null && book.viewer.can_push ? (
          <div className="seqbuilder" hidden={openPanel !== 'builder'}>
            <SequenceTab row={sendableRow} getToken={getToken} active={active && openPanel === 'builder'} seedContactIds={builderSeed} />
          </div>
        ) : null}
      </PanelSection>
    </>
  );
}
